import useFormat from "hooks/useFormat";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { useColorScaleVDP, VerticalDotPlotAuto } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { metadata } from "../data/data_learning_fig_ASSESS";
import { datasets } from "../data/data_learning_fig_ASSESS_datasets";
import { Gender, getGenderName, mkGemEntityDecoder, ordGender, useButtonGroupState, useFigureControlItems, useFigureState, useNamedEntities } from "../domain";
import { useMedia } from "../hooks";
import { unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, identity, io, Ord } from "../prelude";
export * from "../data/data_learning_fig_ASSESS";
const DEFAULT_INDICATOR = "READ";
const Indicator = io.keyof({
    READ: null,
    MATH: null
});
const indicatorLookup = {
    READ: defineMessage({
        id: "fig.learning.ASSESS.READ",
        defaultMessage: "Reading"
    }),
    MATH: defineMessage({
        id: "fig.learning.ASSESS.MATH",
        defaultMessage: "Mathematics"
    })
};
const indicatorOrd = {
    READ: 0,
    MATH: 1
};
export const Data = mkGemEntityDecoder([], {
    ind_id: Indicator,
    gender: Gender,
    value: io.number
});
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const [selectedIndicator] = useButtonGroupState("indicator");
    const entityData_ = useNamedEntities(dataRaw);
    const entityData = React.useMemo(()=>{
        const data = entityData_.map((x)=>({
                ...x,
                gender_name: getGenderName(intl, x.gender),
                ind_name: intl.formatMessage(indicatorLookup[x.ind_id])
            }));
        return Ar.sortBy([
            Ord.contramap((x)=>indicatorOrd[x.ind_id])(Ord.ordNumber),
            Ord.contramap((x)=>x.gender)(ordGender),
            Ord.contramap((x)=>x.value)(Ord.ordNumber)
        ])(data);
    }, [
        intl,
        entityData_
    ]);
    const indicatorControlItems = useFigureControlItems(entityData, (x)=>x.ind_id, (x)=>intl.formatMessage(indicatorLookup[x.ind_id]));
    React.useEffect(()=>{
        actions.setSelectionControl("indicator", {
            type: "ButtonGroup",
            selected: DEFAULT_INDICATOR
        }, [
            ...indicatorControlItems
        ].reverse());
    }, [
        actions,
        indicatorControlItems
    ]);
    const chartData = React.useMemo(()=>{
        return unsafeFromArray(entityData.filter((x)=>x.ind_id === selectedIndicator));
    }, [
        entityData,
        selectedIndicator
    ]);
    const getColor = React.useCallback((x)=>x.gender_name, []);
    const getValue = React.useCallback((x)=>x.value, []);
    const getX = React.useCallback((x)=>x.entity_name, []);
    const getColorPalette = React.useCallback(()=>M.colorRanges.discrete, []);
    const formatY = useFormat(".0%");
    const [colorScale, colorLegendValues] = useColorScaleVDP({
        data: chartData,
        getColor,
        colorPalette: getColorPalette,
        formatColor: identity
    });
    return <VerticalDotPlotAuto height={screenMDown ? M.chartHeight.s : M.chartHeight.m} data={chartData} getX={getX} getValue={getValue} getColor={getColor} formatX={identity} formatValue={formatY} domainY={[
        0,
        1
    ]} colorScale={colorScale} colorLegendValues={colorLegendValues} showTooltip showLegend dotStyle={"small"} labelStyleX="none"/>;
};
export default withFigureIO({
    url: new URL("../data/data_learning_fig_ASSESS.json", import.meta.url).href,
    csv: new URL("../data/data_learning_fig_ASSESS.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_fig_ASSESS.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
